import { useEffect, useState } from 'react';
import { getTrainingAdminCourseDetail } from 'zite-endpoints-sdk';
import { getTrainingAdminCourses } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Video, FileText, HelpCircle, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

type MissingItem = {
  courseTitle: string;
  moduleLabel: string;
  type: 'video' | 'reference' | 'quiz' | 'assessment';
  description: string;
};

export default function MissingContentSection() {
  const [items, setItems] = useState<MissingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMissing = async () => {
      try {
        const { courses } = await getTrainingAdminCourses({});
        const allMissing: MissingItem[] = [];

        for (const course of courses.slice(0, 5)) {
          try {
            const detail = await getTrainingAdminCourseDetail({ courseId: course.id });
            for (const mod of detail.modules) {
              if (!mod.videoUrl) {
                allMissing.push({ courseTitle: course.title, moduleLabel: mod.displayLabel || 'Module', type: 'video', description: 'HeyGen video not recorded' });
              }
              if (!mod.practicalAssessmentItems) {
                allMissing.push({ courseTitle: course.title, moduleLabel: mod.displayLabel || 'Module', type: 'assessment', description: 'Assessment criteria not defined' });
              }
            }
          } catch {}
        }
        setItems(allMissing);
      } catch {}
      setLoading(false);
    };
    loadMissing();
  }, []);

  const icons = { video: Video, reference: FileText, quiz: HelpCircle, assessment: AlertTriangle };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground">Missing Content Register</h2>
        <span className="text-sm text-muted-foreground">{items.length} items outstanding</span>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-50" />
          <p className="text-sm text-muted-foreground">No missing content detected</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => {
            const Icon = icons[item.type];
            return (
              <div key={i} className="flex items-center gap-4 p-4 bg-card border border-border rounded-lg">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  item.type === 'video' ? 'bg-blue-500/10' : item.type === 'assessment' ? 'bg-orange-500/10' : 'bg-muted'
                }`}>
                  <Icon className={`w-4 h-4 ${item.type === 'video' ? 'text-blue-500' : item.type === 'assessment' ? 'text-orange-500' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{item.description}</p>
                  <p className="text-xs text-muted-foreground">{item.courseTitle} → {item.moduleLabel}</p>
                </div>
                <Button size="sm" variant="outline"><Upload className="w-3 h-3 mr-1" /> Resolve</Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
